import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, CheckSquare, Loader2, Pencil, Square, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useBusiness } from '../../contexts/BusinessContext';
import { useTodoViewer } from '../../hooks/useTodoViewer';
import {
  completeTodoItem,
  findTodoItems,
  getTodoLists,
  isTodoOverdue,
  reopenTodoItem,
  type TodoItemWithRelations,
  type TodoList,
} from '../../lib/todos';
import { findEventsForTodos, type CalendarEventRow } from '../../lib/calendarEvents';
import {
  ASSISTANT_OPEN_TODOS_EVENT,
  ASSISTANT_SHOW_TODO_LIST_EVENT,
  openTodosUi,
  type OpenTodosDetail,
  type ShowTodoListDetail,
} from '../../lib/assistant/todoUi';
import { resolveTodoListId } from '../../lib/assistant/todoLookup';
import { ControlCentreTasksModal } from './ControlCentreTasksBadge';
import CustomerDetailsModal from '../CustomerDetailsModal';
import TodoTaskCustomerCard from './TodoTaskCustomerCard';
import type { Customer } from '../../lib/customers';

type FilterTab = 'open' | 'completed';

function personName(row: {
  first_name?: string | null;
  last_name?: string | null;
} | null | undefined): string | null {
  if (!row) return null;
  const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
  return name || null;
}

function formatDue(item: TodoItemWithRelations): string | null {
  if (!item.due_date) return null;
  try {
    const dateLabel = format(parseISO(item.due_date), 'MMM d, yyyy');
    if (item.due_time) {
      const [hh, mm] = item.due_time.slice(0, 5).split(':').map(Number);
      const d = new Date();
      d.setHours(hh, mm, 0, 0);
      return `${dateLabel} · ${format(d, 'h:mm a')}`;
    }
    return dateLabel;
  } catch {
    return item.due_date;
  }
}

// Time-aware: red only once the task's due moment has actually passed
// (past days always; today only after the due time). Shared with the badge.
const isOverdue = (item: TodoItemWithRelations): boolean => isTodoOverdue(item);

/**
 * Global host so the AI (or Control Centre badge) can open Tasks from any screen.
 * - assistant:openTodos → full Tasks drawer (Inbox + all lists)
 * - assistant:showTodoList → focused single-list modal
 */
export default function TasksAssistantHost() {
  const { selectedBusiness } = useBusiness();
  const viewer = useTodoViewer();
  const businessId = selectedBusiness?.id;

  const [fullOpen, setFullOpen] = useState(false);
  const [editTodoId, setEditTodoId] = useState<string | null>(null);
  const [focusOpen, setFocusOpen] = useState(false);
  const [focusList, setFocusList] = useState<TodoList | null>(null);
  const [focusPendingName, setFocusPendingName] = useState<string | null>(null);
  const [focusStatus, setFocusStatus] = useState<FilterTab>('open');
  const [focusError, setFocusError] = useState<string | null>(null);

  useEffect(() => {
    const onOpenTodos = (e: Event) => {
      const detail = (e as CustomEvent<OpenTodosDetail>).detail || {};
      setFocusOpen(false);
      setFocusList(null);
      setFocusPendingName(null);
      setEditTodoId(String(detail.editTodoId || '').trim() || null);
      setFullOpen(true);
    };
    const onShowList = (e: Event) => {
      const detail = (e as CustomEvent<ShowTodoListDetail>).detail || {};
      const pending = String(detail.listName || '').trim() || null;

      // Open the shell immediately so it feels instant
      setFullOpen(false);
      setFocusError(null);
      setFocusList(null);
      setFocusPendingName(pending);
      setFocusStatus(
        detail.status === 'completed' ? 'completed' : 'open'
      );
      setFocusOpen(true);

      void (async () => {
        if (!businessId) {
          setFocusError('No business selected');
          return;
        }
        try {
          const lists = await getTodoLists(businessId, { viewer: viewer || undefined });
          let list: TodoList | null = null;
          if (detail.listId) {
            list = lists.find((l) => l.id === detail.listId) || null;
          }
          if (!list && detail.listName) {
            const resolved = resolveTodoListId(lists, { listName: detail.listName });
            if (resolved.reason.startsWith('ambiguous-list:')) {
              setFocusList(null);
              setFocusPendingName(null);
              setFocusError(
                `Several lists are close — pick one: ${resolved.reason.slice('ambiguous-list:'.length).split('|').join(', ')}`
              );
              return;
            }
            if (resolved.listId) {
              list = lists.find((l) => l.id === resolved.listId) || null;
            }
          }
          if (!list) {
            setFocusList(null);
            setFocusPendingName(null);
            setFocusError(
              detail.listName
                ? `No list close to "${detail.listName}". Available: ${lists.map((l) => l.name).join(', ')}`
                : 'No list specified.'
            );
            return;
          }
          setFocusError(null);
          setFocusPendingName(null);
          setFocusList(list);
        } catch (err) {
          setFocusList(null);
          setFocusPendingName(null);
          setFocusError(err instanceof Error ? err.message : 'Failed to open list');
        }
      })();
    };

    window.addEventListener(ASSISTANT_OPEN_TODOS_EVENT, onOpenTodos);
    window.addEventListener(ASSISTANT_SHOW_TODO_LIST_EVENT, onShowList);
    return () => {
      window.removeEventListener(ASSISTANT_OPEN_TODOS_EVENT, onOpenTodos);
      window.removeEventListener(ASSISTANT_SHOW_TODO_LIST_EVENT, onShowList);
    };
  }, [businessId, viewer]);

  return (
    <>
      <ControlCentreTasksModal
        isOpen={fullOpen}
        onClose={() => {
          setFullOpen(false);
          setEditTodoId(null);
        }}
        editTodoId={editTodoId}
        onEditTodoIdConsumed={() => setEditTodoId(null)}
        onChanged={() => {
          window.dispatchEvent(
            new CustomEvent('todoItemsChanged', { detail: { businessId } })
          );
        }}
      />
      <TodoListFocusModal
        isOpen={focusOpen}
        list={focusList}
        pendingName={focusPendingName}
        initialFilter={focusStatus}
        error={focusError}
        businessId={businessId}
        onClose={() => {
          setFocusOpen(false);
          setFocusList(null);
          setFocusPendingName(null);
          setFocusError(null);
        }}
        onEdit={(item) => {
          setFocusOpen(false);
          setFocusList(null);
          setFocusPendingName(null);
          openTodosUi({ editTodoId: item.id });
        }}
      />
    </>
  );
}

interface TodoListFocusModalProps {
  isOpen: boolean;
  list: TodoList | null;
  pendingName: string | null;
  error: string | null;
  initialFilter: FilterTab;
  businessId: string | undefined;
  onClose: () => void;
  onEdit?: (item: TodoItemWithRelations) => void;
}

function TodoListFocusModal({
  isOpen,
  list,
  pendingName,
  error,
  initialFilter,
  businessId,
  onClose,
  onEdit,
}: TodoListFocusModalProps) {
  const [filter, setFilter] = useState<FilterTab>(initialFilter);
  const [items, setItems] = useState<TodoItemWithRelations[]>([]);
  const [linkedEvents, setLinkedEvents] = useState<Map<string, CalendarEventRow>>(new Map());
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [customerForDetails, setCustomerForDetails] = useState<Customer | null>(null);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const load = useCallback(async () => {
    if (!businessId || !list) {
      setItems([]);
      setLinkedEvents(new Map());
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await findTodoItems(businessId, {
        list_id: list.id,
        status: filter,
        limit: 200,
      });
      setItems(rows);
      try {
        setLinkedEvents(await findEventsForTodos(businessId, rows.map((r) => r.id)));
      } catch {
        setLinkedEvents(new Map());
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [businessId, list, filter]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setFilter(initialFilter);
      document.body.style.overflow = 'hidden';
      void load();
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !list) return;
    void load();
  }, [filter, isOpen, list, load]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (customerDetailsOpen) {
        setCustomerDetailsOpen(false);
        setCustomerForDetails(null);
        return;
      }
      onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose, customerDetailsOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onChanged = () => void load();
    window.addEventListener('todoItemsChanged', onChanged);
    return () => window.removeEventListener('todoItemsChanged', onChanged);
  }, [isOpen, load]);

  const handleToggle = async (item: TodoItemWithRelations) => {
    if (!businessId || togglingId) return;
    setTogglingId(item.id);
    try {
      if (item.status === 'open') {
        await completeTodoItem(businessId, item.id);
      } else {
        await reopenTodoItem(businessId, item.id);
      }
      window.dispatchEvent(
        new CustomEvent('todoItemsChanged', { detail: { businessId, todoId: item.id } })
      );
      await load();
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to update task');
    } finally {
      setTogglingId(null);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const title = list?.name || pendingName || 'Tasks';
  const resolving = isOpen && !list && !error && !!pendingName;
  const displayError = error || loadError;

  const modalContent = (
    <>
      <div
        className={`fixed inset-0 transition-opacity duration-200 ${
          isOpen ? 'bg-black/50' : 'bg-black/0'
        }`}
        style={{
          zIndex: 99999,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
        }}
        onClick={(e) => {
          if (e.target !== e.currentTarget) return;
          onClose();
        }}
        onTransitionEnd={() => {
          if (!isOpen) setIsAnimating(false);
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="todo-list-focus-title"
      >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[560px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 99999,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 shrink-0 relative">
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="pr-10">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <CheckSquare className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <h2
                    id="todo-list-focus-title"
                    className="text-2xl font-bold text-gray-900 truncate"
                  >
                    {title}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {resolving
                      ? 'Opening…'
                      : list
                        ? 'Tasks in this list'
                        : 'Could not open that list'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {list ? (
            <div className="px-6 pt-4 pb-2 shrink-0">
              <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                {(['open', 'completed'] as FilterTab[]).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFilter(tab)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-md capitalize transition-colors ${
                      filter === tab
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex-1 overflow-y-auto px-4 pb-6 min-h-0">
            {displayError ? (
              <p className="mx-2 mt-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {displayError}
              </p>
            ) : null}

            {resolving || (!list && !displayError) ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
              </div>
            ) : !list ? null : loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-gray-300" />
              </div>
            ) : items.length === 0 ? (
              <p className="px-3 py-10 text-center text-sm text-gray-500">
                {filter === 'open' ? 'No open tasks in this list' : 'No completed tasks here'}
              </p>
            ) : (
              <ul className="rounded-xl border border-gray-200 bg-white overflow-hidden divide-y divide-gray-100">
                {items.map((item) => {
                  const due = formatDue(item);
                  const overdue = isOverdue(item);
                  const busy = togglingId === item.id;
                  const linked = linkedEvents.get(item.id);
                  const scheduledLabel = linked
                    ? (() => {
                        try {
                          return `Scheduled ${format(parseISO(linked.start_date), 'MMM d')} · ${String(linked.start_time).slice(0, 5)}`;
                        } catch {
                          return `Scheduled ${linked.start_date}`;
                        }
                      })()
                    : null;
                  return (
                    <li key={item.id} className="flex items-start gap-2 px-3 py-3 hover:bg-gray-50">
                      <button
                        type="button"
                        onClick={() => void handleToggle(item)}
                        disabled={busy}
                        className="mt-0.5 shrink-0 text-gray-400 hover:text-emerald-600 disabled:opacity-50"
                        aria-label={item.status === 'open' ? 'Mark complete' : 'Reopen task'}
                      >
                        {busy ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : item.status === 'completed' ? (
                          <Check className="h-5 w-5 text-emerald-600" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-sm font-medium leading-snug ${
                            item.status === 'completed'
                              ? 'text-gray-400 line-through'
                              : 'text-gray-900'
                          }`}
                        >
                          {item.title}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                          {due ? (
                            <span className={overdue ? 'text-red-600 font-medium' : ''}>
                              {overdue ? `Overdue · ${due}` : due}
                            </span>
                          ) : null}
                          {scheduledLabel ? (
                            <span className="text-emerald-700 font-medium">{scheduledLabel}</span>
                          ) : null}
                          {personName(item.assignee) ? <span>{personName(item.assignee)}</span> : null}
                        </div>
                        {item.description ? (
                          <p className="mt-1 text-xs text-gray-500 line-clamp-2">{item.description}</p>
                        ) : null}
                        {item.customer ? (
                          <TodoTaskCustomerCard
                            customer={item.customer}
                            businessId={businessId}
                            onOpenCustomer={(customer) => {
                              setCustomerForDetails(customer);
                              setCustomerDetailsOpen(true);
                            }}
                          />
                        ) : null}
                      </div>
                      {onEdit ? (
                        <button
                          type="button"
                          onClick={() => onEdit(item)}
                          className="mt-0.5 shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                          aria-label={`Edit ${item.title}`}
                          title="Edit task"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
      </div>

      <CustomerDetailsModal
        isOpen={customerDetailsOpen}
        onClose={() => {
          setCustomerDetailsOpen(false);
          setCustomerForDetails(null);
        }}
        customer={customerForDetails}
        onUpdate={() => void load()}
        zIndex={100400}
      />
    </>
  );

  return createPortal(modalContent, document.body);
}
